"use client";
import { Select } from "antd";
import type { SelectProps } from "antd";
import { useEffect, useMemo, useReducer, useRef } from "react";

type FetchMethod = "GET" | "POST";

interface AppSelectProps extends Omit<SelectProps, "options" | "loading" | "onSelect"> {
  // Fetching props
  url?: string;
  method?: FetchMethod;
  body?: Record<string, unknown>;
  headers?: Record<string, string>;
  dataSource?: Record<string, unknown>[];
  /** Server-action option source. Preferred over `url`. Returns the raw rows. */
  loader?: () => Promise<any[] | { data: any[] }>;
  // Transform props
  labelField?: string | ((item: Record<string, unknown>) => string); // Default: "name" or "label"
  valueField?: string; // Default: "id"
  onSelect?: (value: any, option: any, fullItem: Record<string, unknown>) => void;
  // Callback props
  onSourceLoaded?: (data: Record<string, unknown>[]) => Record<string, unknown>[] | void;
}

// -- fetch reducer ---------------------------------------------------------

type FetchState = {
  data: Record<string, unknown>[];
  loading: boolean;
  error: string | null;
};

type FetchAction =
  | { type: "LOADING" }
  | { type: "SUCCESS"; payload: Record<string, unknown>[] }
  | { type: "ERROR"; payload: string };

function fetchReducer(state: FetchState, action: FetchAction): FetchState {
  switch (action.type) {
    case "LOADING":
      return { ...state, loading: true, error: null };
    case "SUCCESS":
      return { data: action.payload, loading: false, error: null };
    case "ERROR":
      return { ...state, loading: false, error: action.payload };
  }
}

function extractArray(raw: unknown): Record<string, unknown>[] {
  if (Array.isArray(raw)) return raw as Record<string, unknown>[];
  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    for (const key of ["data", "items", "rows", "results"]) {
      if (Array.isArray(obj[key])) return obj[key] as Record<string, unknown>[];
    }
  }
  return [];
}

// -- component ---------------------------------------------------------------

function AppSelect({
  url,
  loader,
  method = "GET",
  body,
  headers,
  dataSource,
  labelField,
  valueField = "id",
  onSourceLoaded: onDataSourceChange,
  onSelect,
  ...props
}: AppSelectProps) {
  const [{ data: fetchedData, loading }, dispatch] = useReducer(fetchReducer, {
    data: [],
    loading: false,
    error: null,
  });

  const bodyRef = useRef(body);
  const headersRef = useRef(headers);
  const loaderRef = useRef(loader);
  const onSourceLoadedRef = useRef(onDataSourceChange);
  const onChangeRef = useRef(props.onChange);
  const prevValueRef = useRef<any>(Symbol("initial"));

  useEffect(() => {
    bodyRef.current = body;
    headersRef.current = headers;
    loaderRef.current = loader;
    onSourceLoadedRef.current = onDataSourceChange;
    onChangeRef.current = props.onChange;
  });

  // Trigger onChange when value is provided initially or changes
  useEffect(() => {
    if (props.value !== prevValueRef.current && onChangeRef.current) {
      prevValueRef.current = props.value;
      onChangeRef.current(props.value);
    }
  }, [props.value]);

  useEffect(() => {
    if (dataSource) return;
    if (!url && !loaderRef.current) return;
    const controller = new AbortController();

    async function load() {
      dispatch({ type: "LOADING" });
      try {
        // Preferred path: a server action supplies the options.
        if (loaderRef.current) {
          const raw = await loaderRef.current();
          dispatch({ type: "SUCCESS", payload: extractArray(raw) });
          return;
        }

        const isPost = method === "POST";
        const res = await fetch(url!, {
          method,
          signal: controller.signal,
          headers: {
            ...(isPost ? { "Content-Type": "application/json" } : {}),
            ...headersRef.current,
          },
          body: isPost && bodyRef.current ? JSON.stringify(bodyRef.current) : undefined,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
        const raw = await res.json();
        const items = extractArray(raw);
        dispatch({ type: "SUCCESS", payload: items });
      } catch (e) {
        if ((e as { name?: string }).name !== "AbortError") {
          dispatch({ type: "ERROR", payload: String(e) });
        }
      }
    }

    load();
    return () => controller.abort();
  }, [url, method, dataSource]);

  // Transform data into Select options — memoized so onSourceLoaded doesn't cause infinite loops
  const items = useMemo(() => {
    const rawItems = dataSource ?? fetchedData;
    return onSourceLoadedRef.current ? (onSourceLoadedRef.current(rawItems) ?? rawItems) : rawItems;
  }, [dataSource, fetchedData]);

  const selectOptions = items.map((item) => ({
    label: typeof labelField === "function"
      ? labelField(item)
      : String(item[labelField || "name"] ?? ""),
    value: String(item[valueField] ?? ""),
  }));

  // Create a lookup map from value to full item data
  const itemMap = new Map(
    items.map((item) => [String(item[valueField] ?? ""), item])
  );

  // Wrap onChange to pass full row data as last argument
  const handleChange = (value: any) => {
    const fullItem = itemMap.get(String(value));
    if (props.onChange) {
      (props.onChange as (value: any, option: any, fullItem: any) => void)(
        value,
        { [valueField]: value },
        fullItem
      );
    }
  };

  return (
    <Select allowClear 
      style={{ width: "100%" }}
      loading={loading}
      options={selectOptions} 
      onSelect={(value, option) => {
        const fullItem = itemMap.get(String(value));
        if (onSelect) {
          onSelect(value, option, fullItem!);
        }
      }}
      {...props}
      onChange={handleChange}
    />
  );
}

export { AppSelect };
